import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type * as React from "react";
import FormTemplateWrapper from "components/v3/sharedComponents/forms/FormTemplateWrapper";
import {
  AGREEMENT_FORM_TEMPLATE_ROOT_CLASS,
  getAgreementFormSurveyScopeSx
} from "components/v3/sharedComponents/forms/agreementFormSurveyScopeSx";
import { COLORS } from "components/v3/Theme/colors";

const agreementModalChromeGradient = `linear-gradient(to bottom, #F2F7FB, ${COLORS.WHITE})`;

export { AGREEMENT_FORM_TEMPLATE_ROOT_CLASS };

export type AgreementFormDialogProps = {
  formId: string;
  initialConfirmationChecked?: boolean;
  onAgreed: () => void;
  onClose: () => void;
  open: boolean;
  title: string;
};

const AgreementFormDialog: React.FC<AgreementFormDialogProps> = ({
  formId,
  initialConfirmationChecked = false,
  onAgreed,
  onClose,
  open,
  title
}) => {
  return (
    <Dialog
      BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(17, 24, 39, 0.45)"
        }
      }}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      scroll="paper"
      PaperProps={{
        sx: {
          background: agreementModalChromeGradient,
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          height: "min(calc(95vh - 32px), calc(100dvh - 24px))",
          maxHeight: "min(calc(95vh - 32px), calc(100dvh - 24px))",
          my: 1,
          mx: 1,
          overflow: "hidden",
          pb: "env(safe-area-inset-bottom, 0px)",
          width: "100%"
        }
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          backgroundColor: "transparent",
          boxSizing: "border-box",
          display: "flex",
          flexShrink: 0,
          justifyContent: "space-between",
          pb: 1,
          pr: 1,
          pt: 2,
          px: 2
        }}
      >
        <Typography component="span" variant="h5_black">
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          edge="end"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          // Override MUI flex: 1 1 auto + overflowY: auto so content cannot grow the Paper past the viewport
          backgroundColor: "transparent",
          boxSizing: "border-box",
          display: "flex",
          flex: "1 1 0%",
          flexDirection: "column",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          pb: 2,
          pt: 0,
          px: 2
        }}
      >
        <Typography
          component="p"
          variant="body_sm_dark_gray"
          sx={{ flexShrink: 0, mb: 2, mt: 0 }}
        >
          Review the document below, then complete the form to continue.
        </Typography>
        <Box
          className="agreement-form-dialog-survey-scope"
          sx={{
            border: `1px solid ${COLORS.PRIMARY_LIGHTER}`,
            ...getAgreementFormSurveyScopeSx({
              scrollContentMaxHeight:
                "calc(min(100vh, 100dvh) - 410px) !important"
            })
          }}
        >
          <FormTemplateWrapper
            formId={formId}
            preCheckConfirmationQuestion={initialConfirmationChecked}
            templateRootClassName={AGREEMENT_FORM_TEMPLATE_ROOT_CLASS}
            onComplete={() => {
              onAgreed();
              onClose();
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AgreementFormDialog;
